// src/components/CarouselComponent.js
import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { usePublicContent } from '../lib/publicContent';
import { resolveMediaUrl } from '../lib/mediaUrl';

const AUTOPLAY_MS = 6500;

const CarouselComponent = () => {
  const { content } = usePublicContent();
  const slides = useMemo(() => content?.carousel?.slides || [], [content]);
  const hasSlides = slides.length > 0;

  const viewportRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  // Scroll the internal viewport horizontally (no page scroll, no hash)
  const scrollToIndex = useCallback((i) => {
    const vp = viewportRef.current;
    if (!vp) return;
    const target = vp.querySelector(`#css-slide-${i + 1}`);
    if (!target) return;
    vp.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
  }, []);

  // Autoplay — scrolls the viewport only, never touches the URL
  useEffect(() => {
    if (!hasSlides || paused) return;
    const t = setTimeout(() => {
      const next = (index + 1) % slides.length;
      scrollToIndex(next);
      setIndex(next);
    }, AUTOPLAY_MS);
    return () => clearTimeout(t);
  }, [hasSlides, paused, index, slides.length, scrollToIndex]);

  if (!hasSlides) return null;

  const slideId = (i) => `css-slide-${i + 1}`;
  const prevIdx = (i) => (i - 1 + slides.length) % slides.length;
  const nextIdx = (i) => (i + 1) % slides.length;

  return (
    <section
      id="main-slide"
      className="css-carousel"
      aria-label="Hero gallery"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <ol className="css-carousel__viewport" ref={viewportRef}>
        {slides.map((s, i) => (
          <li
            key={i}
            id={slideId(i)}
            tabIndex={0}
            className="css-carousel__slide"
            style={{ backgroundImage: `url(${resolveMediaUrl(s.imageUrl)})` }}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${slides.length}`}
          >
            <div className="css-carousel__snapper">
              {/* Prev / Next as BUTTONS (no href, no hash change) */}
              <button
                type="button"
                className="css-carousel__prev"
                aria-label="Previous slide"
                onClick={() => {
                  const n = prevIdx(i);
                  scrollToIndex(n);
                  setIndex(n);
                }}
              />
              <button
                type="button"
                className="css-carousel__next"
                aria-label="Next slide"
                onClick={() => {
                  const n = nextIdx(i);
                  scrollToIndex(n);
                  setIndex(n);
                }}
              />
            </div>

            <div className="carousel-caption">
              <div className="container">
                <div className="row">
                  <div className="col-lg-12">
                    {s.heading1 && <h1 className={`heading ${s.animation1 || ''}`}>{s.heading1}</h1>}
                    {s.heading2 && <h1 className={`heading heading2 ${s.animation2 || ''}`}>{s.heading2}</h1>}
                    {s.ctaText && (
                      <a href={s.ctaHref || '#'} className={`btn btn-common btn-lg ${s.ctaAnimation || ''}`}>
                        {s.ctaText}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
};

export default CarouselComponent;
